import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ErrorBanner, formatUtc, useAsync } from '../../components/Async'
import { Dialog } from '../../components/Dialog'
import { ApiError, alerts, api, session, type AlertDetail } from '../../lib/api'
import { AccountSummary } from '../events/AccountSummary'
import { readAccount } from '../events/account'
import { severityIdFromName, severityLook } from '../../lib/severity'
import { EvidencePanel } from './EvidencePanel'
import { CapturedExcerpt } from '../cases/CapturedExcerpt'
import { DraftPolicyPanel } from '../cases/DraftPolicyPanel'

/**
 * One alert, and the four things an analyst can do about it.
 *
 * The timeline is the substance of the page. An alert says "this happened thirty times"; the
 * question that follows is always "when, and did it stop", and a count alone cannot answer it.
 *
 * Resolving asks for a sentence and will not proceed without one. That is not ceremony: the next
 * person to see this pattern is the audience, and "resolved" with no reason tells them nothing except
 * that somebody wanted the row gone.
 */
export function AlertDetailPage() {
  const { alertId } = useParams<{ alertId: string }>()
  const [detail, setDetail] = useState<AlertDetail | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [resolution, setResolution] = useState('')
  const [suppressReason, setSuppressReason] = useState('')

  /**
   * Which of the two decisions is being taken, if either.
   *
   * Both are writes somebody has to mean, and both used to sit in the sidebar with their fields
   * permanently open - so the page read as a form to fill in rather than as an alert to understand.
   * The timeline is what this screen is for; these are what you do once you have read it.
   */
  const [deciding, setDeciding] = useState<'resolve' | 'suppress' | null>(null)

  const load = useCallback(async () => {
    if (!alertId) return
    try {
      setDetail(await alerts.get(alertId))
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The alert could not be loaded.')
    }
  }, [alertId])

  useEffect(() => {
    void load()
  }, [load])

  const run = useCallback(
    async (work: () => Promise<string>) => {
      setBusy(true)
      setFailure(null)
      setNotice(null)
      try {
        setNotice(await work())
        await load()
      } catch (error) {
        setFailure(error instanceof ApiError ? error.message : 'The request failed.')
      } finally {
        setBusy(false)
      }
    },
    [load]
  )

  const acknowledge = () =>
    run(async () => {
      await alerts.move(alertId!, 'acknowledged')
      return 'Acknowledged.'
    })

  const resolve = () =>
    run(async () => {
      await alerts.move(alertId!, 'resolved', { resolution })
      setResolution('')
      setDeciding(null)
      return 'Resolved. The same pattern happening again will open a new alert.'
    })

  const suppress = () =>
    run(async () => {
      if (!detail) throw new ApiError(400, 'No alert is open.')
      const result = await alerts.suppress({
        deviceId: detail.summary.deviceId,
        agentId: detail.summary.agentId ?? undefined,
        ruleName: detail.summary.matchedRuleNames?.split(',')[0]?.trim(),
        reason: suppressReason,
        days: 30
      })
      setSuppressReason('')
      setDeciding(null)
      return `Suppressed until ${result.expiresUtc ? formatUtc(result.expiresUtc) : 'it is removed'}.`
    })

  if (!detail) {
    return (
      <>
        <ErrorBanner message={failure} />
        <div className="empty">Loading…</div>
      </>
    )
  }

  const { summary } = detail
  const open = summary.state !== 'resolved'

  return (
    <>
      <div className="page-header">
        <div>
          <h1 data-testid="alert-title">{summary.title}</h1>
          <p>
            <span className={`severity ${severityLook(severityIdFromName(summary.severity)).className}`}>
              <span className="severity-bar" aria-hidden="true" />
              {severityLook(severityIdFromName(summary.severity)).label}
            </span>{' '}
            <span className="pill" data-testid="alert-state">
              {summary.state}
            </span>{' '}
            · seen {summary.occurrenceCount === 1 ? 'once' : `${summary.occurrenceCount} times`} between{' '}
            {formatUtc(summary.firstSeenUtc)} and {formatUtc(summary.lastSeenUtc)}
          </p>
        </div>
        {open && summary.state === 'new' && (
          <button type="button" className="primary" onClick={acknowledge} disabled={busy} data-testid="acknowledge-alert">
            Acknowledge
          </button>
        )}
      </div>

      <ErrorBanner message={failure} />
      {notice && (
        <div className="banner success" role="status" data-testid="alert-notice">
          {notice}
        </div>
      )}

      <div className="editor-grid">
        <div className="card">
          <div className="card-body">
            {/* Collapsed, and it starts closed.
                An alert exists to fold repetition into one case — "a count of thirty is one incident,
                not thirty" — and the detail page then unfolded it into a hundred near-identical rows,
                pushing Context, Captures, Resolve and Suppress below all of them. Triaging a case meant
                scrolling past a hundred lines of the same sentence to reach the button that ends it.
                The header already carries the count and the span, which is most of what the timeline
                adds (D-275). */}
            <details className="subsection" data-testid="occurrence-timeline">
              <summary>
                When it happened — {summary.occurrenceCount === 1 ? 'once' : `${summary.occurrenceCount} times`}
                {detail.occurrences.length < summary.occurrenceCount
                  ? `, showing the most recent ${detail.occurrences.length}`
                  : ''}
              </summary>
            <div className="table-scroll">
              <table data-testid="occurrence-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Severity</th>
                    <th className="secondary">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.occurrences.map((occurrence) => (
                    <tr key={occurrence.eventRecordId}>
                      <td>
                        <Link to={`/events/${occurrence.eventRecordId}`}>
                          {formatUtc(occurrence.occurredUtc)}
                        </Link>
                      </td>
                      <td>
                        <span className={`severity ${severityLook(severityIdFromName(occurrence.severity)).className}`}>
                          <span className="severity-bar" aria-hidden="true" />
                          {severityLook(severityIdFromName(occurrence.severity)).label}
                        </span>
                      </td>
                      <td className="muted secondary">{occurrence.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {detail.occurrences.length < summary.occurrenceCount && (
              <p className="hint" data-testid="occurrence-truncated">
                The server returns the most recent {detail.occurrences.length} of{' '}
                {summary.occurrenceCount}. The count above is the true one; this list is a sample of it.
              </p>
            )}
            </details>

            {detail.history.length > 0 && (
              <>
                <h3>What was done</h3>
                <div data-testid="alert-history">
                  {detail.history.map((entry, index) => (
                    <div className="finding" key={`${entry.changedUtc}-${index}`}>
                      <code>{entry.toState}</code>
                      <div>
                        <div>
                          from {entry.fromState} · {formatUtc(entry.changedUtc)}
                        </div>
                        {entry.note && <div className="muted">{entry.note}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div>
          <div className="card">
            <div className="card-body">
              <h3 style={{ marginTop: 0 }}>Context</h3>
              <dl className="detail">
                <dt>Assistant</dt>
                <dd>{summary.agentId ?? 'unknown'}</dd>
                <dt>Process</dt>
                <dd>{detail.processName ?? 'unknown'}</dd>
                <dt>Rules</dt>
                <dd>{summary.matchedRuleNames ?? 'none recorded'}</dd>
                <dt>Device</dt>
                <dd>
                  <DeviceLink deviceId={summary.deviceId} />
                </dd>
              </dl>
            </div>
          </div>

          {/* Composed here rather than served by the alert. Alerting deliberately reads no other
              context's tables (ADR-S-027), so the account lives on the events the alert folded and the
              browser is where the two meet -- the same shape the capture panel uses. */}
          {/*
            What was actually captured, on the case rather than two clicks away on an event page - the
            thing that turns "a rule matched" into "here is what was sent".
            
            Asked for by case rather than by occurrence, because the text is not on this case's own
            events: a case is raised from a Detection Finding and the words arrive as an Agent Prompt
            Observation, a separate delivery from the same capability. The server matches them on the
            tuple the fold already uses.
          */}
          <CapturedExcerpt alertId={summary.alertId} />

          {/*
            The loop closes here. An analyst reads what happened and an author asks for a policy that
            would catch it again - so the control is offered to an author and hidden from an analyst,
            which is a deliberate asymmetry on a screen an analyst lives in. What comes back is a
            draft; a person reviews it, scopes it, and approves it.
          */}
          <DraftPolicyPanel alertId={summary.alertId} roles={session.identity()?.roles ?? []} />

          <AlertAccount eventIds={detail.occurrences.map((occurrence) => occurrence.eventRecordId)} />

          <EvidencePanel
            deviceId={summary.deviceId}
            eventIds={detail.occurrences.map((occurrence) => occurrence.eventRecordId)}
          />

          {open ? (
            <div className="card">
              <div className="card-body">
                <h3 style={{ marginTop: 0 }}>Decide</h3>
                <p className="muted" style={{ marginTop: 0 }}>
                  Read the timeline first. Both of these are final in their own way: resolving cannot
                  be reopened, and suppressing stops this rule reaching you on this device.
                </p>
                <div className="toolbar">
                  <button
                    type="button"
                    className="primary"
                    onClick={() => setDeciding('resolve')}
                    disabled={busy}
                    data-testid="open-resolve"
                  >
                    Resolve
                  </button>
                  <button type="button" onClick={() => setDeciding('suppress')} disabled={busy} data-testid="open-suppress">
                    Stop alerting on this
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body">
                <h3 style={{ marginTop: 0 }}>Resolved</h3>
                <p data-testid="alert-resolution-text">{detail.resolution}</p>
                <p className="muted">{detail.resolvedUtc && formatUtc(detail.resolvedUtc)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Both dialogs, rather than the sidebar cards these used to be. The fields belong to a
          decision somebody has come to, and a textarea that is always open invites the decision to be
          taken before the timeline beside it has been read. */}
      <Dialog
        open={deciding === 'resolve'}
        title="Resolve this alert"
        onClose={() => setDeciding(null)}
        data-testid="resolve-dialog"
      >
        <form
          data-testid="resolve-form"
          onSubmit={(event) => {
            event.preventDefault()
            void resolve()
          }}
        >
          <div className="field">
            <label htmlFor="resolution">What was decided</label>
            <textarea
              id="resolution"
              value={resolution}
              onChange={(event) => setResolution(event.target.value)}
              placeholder="The data owner approved this transfer."
              data-testid="alert-resolution"
              required
            />
            <div className="hint">
              Resolving is final. The same pattern happening again opens a new alert rather than
              reopening this one.
            </div>
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={() => setDeciding(null)} disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary"
              disabled={busy || resolution.trim().length === 0}
              data-testid="resolve-alert"
            >
              Resolve
            </button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={deciding === 'suppress'}
        title="Stop alerting on this"
        onClose={() => setDeciding(null)}
        data-testid="suppress-dialog"
      >
        <form
          data-testid="suppress-form"
          onSubmit={(event) => {
            event.preventDefault()
            void suppress()
          }}
        >
          <div className="field">
            <label htmlFor="suppress-reason">Why this is accepted</label>
            <input
              id="suppress-reason"
              value={suppressReason}
              onChange={(event) => setSuppressReason(event.target.value)}
              placeholder="The team pastes their own service keys deliberately."
              data-testid="suppress-reason"
              required
            />
            <div className="hint">
              Suppresses this rule, on this device, for thirty days. It lapses on its own &mdash; an
              indefinite mute is how a detection product ends up detecting nothing.
            </div>
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={() => setDeciding(null)} disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || suppressReason.trim().length === 0}
              data-testid="suppress-alert"
            >
              Suppress for 30 days
            </button>
          </div>
        </form>
      </Dialog>
    </>
  )
}

/**
 * The account behind an alert, read from the first occurrence that carries one.
 *
 * The first rather than all of them: an alert folds occurrences that share a device, an assistant and
 * a rule set, so they are the same person on the same machine. If that ever stops being true the
 * dedupe key is what changed, and this would be the least of it.
 */
function AlertAccount({ eventIds }: { eventIds: string[] }) {
  const key = eventIds.join(',')
  const attributes = useAsync(async () => {
    for (const eventId of key === '' ? [] : key.split(',')) {
      const event = await api.getEvent(eventId)
      if (readAccount(event.attributesJson)) return event.attributesJson
    }
    return null
  }, [key])

  return attributes.value ? <AccountSummary attributesJson={attributes.value} /> : null
}

/**
 * The device an alert is about, by name and reachable.
 *
 * It was a raw UUID that could not be clicked, on a page whose own heading names the machine — the
 * least readable identifier available and the only one that led nowhere. The name is fetched here
 * rather than carried on the alert because the alerting context reads no fleet table (ADR-S-027), and
 * the browser is already the place this page composes across that boundary for the account panel.
 *
 * Falls back to the id if the lookup fails: a link that works with an ugly label beats no link.
 */
function DeviceLink({ deviceId }: { deviceId: string }) {
  const device = useAsync(async () => {
    try {
      return await api.getDevice(deviceId)
    } catch {
      return null
    }
  }, [deviceId])

  return (
    <Link to={`/devices/${deviceId}`} data-testid="alert-device-link">
      {device.value?.device.deviceName ?? deviceId}
    </Link>
  )
}
